// app/src/features/layout/DeleteConfirmModal.jsx
import { deleteForecast } from "../../commands/deleteForecast.js";
import { C } from "../../lib/colors.js";
import { surface, ui } from "../../lib/telemetry/instrument.js";

/**
 * Renders while `deleteConfirm` names a forecast or a batch; the Delete button runs the command for
 * each and drops them from the stacks and the saved order.
 */
export default function DeleteConfirmModal({
  deleteConfirm,
  setDeleteConfirm,
  mkDeleteDeps,
  forecastStacking,
  removeFromForecastOrder,
}) {
  if (!deleteConfirm) return null;
  return (
    <div
      onClick={() => setDeleteConfirm(null)}
      // Keyed by the forecast only when it is one; a batch delete has no single key.
      {...surface("delete-forecast", deleteConfirm.id)}
      {...ui("delete-forecast.dismiss")}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 600,
        background: C.modalOverlay,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        {...ui("delete-forecast.dialog")}
        style={{
          background: C.panel,
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          padding: 24,
          width: 420,
          maxWidth: "92vw",
          boxShadow: C.shadowBox,
        }}
      >
        <div
          style={{
            fontSize: 15,
            color: C.white,
            marginBottom: 8,
            fontWeight: 600,
          }}
        >
          {Array.isArray(deleteConfirm) && deleteConfirm.length > 1
            ? `Delete ${deleteConfirm.length} forecasts?`
            : "Delete this forecast?"}
        </div>
        <div
          style={{
            fontSize: 13,
            color: C.white,
            lineHeight: 1.5,
            marginBottom: 6,
            fontWeight: "bold",
          }}
        >
          {[]
            .concat(deleteConfirm)
            .slice(0, 5)
            .map((d) => (
              <div key={d.id}>{d.question || d.contract?.title || "Untitled"}</div>
            ))}
          {[].concat(deleteConfirm).length > 5 && (
            <div style={{ fontWeight: 400, color: C.grayL }}>
              and {[].concat(deleteConfirm).length - 5} more
            </div>
          )}
        </div>
        <div style={{ fontSize: 12, color: C.grayL, lineHeight: 1.5, marginBottom: 16 }}>
          {[].concat(deleteConfirm).length > 1 ? "They" : "It"} will be removed from your forecasts.
          You can&apos;t undo this.
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            data-popup-cancel
            onClick={() => setDeleteConfirm(null)}
            {...ui("delete-forecast.cancel")}
            style={{
              background: C.btnBg,
              color: C.grayL,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: "6px 14px",
              fontFamily: '"IBM Plex Mono",monospace',
              fontSize: 12,
              cursor: "pointer",
              letterSpacing: 0,
            }}
          >
            Cancel
          </button>
          <button
            data-popup-primary
            {...ui("delete-forecast.confirm", deleteConfirm.id)}
            onClick={() => {
              // One forecast from a card, or several picked for a batch delete.
              for (const d of [].concat(deleteConfirm)) {
                deleteForecast(mkDeleteDeps(), {
                  runId: d.id,
                  batch: [].concat(deleteConfirm).length,
                });
                forecastStacking.onCardDeleted(d.id);
                removeFromForecastOrder(d.id);
              }
              setDeleteConfirm(null);
            }}
            style={{
              background: C.red,
              color: C.btnText || "#fff",
              border: "none",
              borderRadius: 8,
              padding: "6px 14px",
              fontFamily: '"IBM Plex Mono",monospace',
              fontSize: 12,
              cursor: "pointer",
              fontWeight: "bold",
              letterSpacing: 0,
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
